/**
 * Berichts-Markup für das Einfügen in Outlook aufbereiten.
 *
 * Der Bericht sieht im Browser richtig aus, in der eingefügten Mail aber nicht:
 * **Outlook (Word-Rendering) verwirft `style="color:…"` an Textelementen.**
 * Übrig bleibt schwarze Schrift — Eyebrow, Budget-Ampel, Status-Pillen und
 * Ticket-Nummern verlieren ihre Farbe, und der Bericht wirkt wie ein
 * Rohausdruck.
 *
 * Die abgelöste App kannte das und setzte Farben deshalb **doppelt**: als CSS
 * für den Browser und als `<font color>` fürs Einfügen — 25 Mal in
 * `report_html.py`, mit dem Kommentar „Farbe via <font>, damit sie beim
 * Einfügen erhalten bleibt". Beim Portieren ist genau das verloren gegangen.
 *
 * Statt die Farbe an rund 50 Stellen der Ansicht zu verdoppeln, passiert es
 * hier an einer Stelle — und nur auf dem Kopierweg. Die Ansicht im Browser
 * bleibt unberührt; sie ist ja in Ordnung.
 *
 * Wirkt zusätzlich auf die von Hand bearbeitete Fassung, die denselben Weg
 * nimmt.
 *
 * **Ohne DOM.** Dieselbe Funktion läuft im Browser (Kopierknopf) und auf dem
 * Server (`renderBerichtMail`, MCP-Werkzeug `statusbericht_als_mail`). Sie
 * parst deshalb mit `htmlparser2` statt über `document.createElement` und
 * serialisiert wie `innerHTML`: Attribute in Originalreihenfolge, leere
 * Attribute als `=""`, im Text nur `&`, `<`, `>` und das geschützte
 * Leerzeichen als Entität.
 */
import { render } from "dom-serializer";
import { type ChildNode, Element, isTag, isText } from "domhandler";
import { parseDocument } from "htmlparser2";


/**
 * Elemente, in die Inline-Inhalt gehört. Ein `<font>` in `<tr>` oder `<table>`
 * wäre ungültiges Markup — der Browser zöge es beim Einfügen aus der Tabelle
 * heraus und die Zeile bräche auseinander.
 */
const WRAPPABLE = new Set([
  "td",
  "th",
  "span",
  "p",
  "a",
  "strong",
  "em",
  "b",
  "i",
  "div",
  "li",
]);

/**
 * `rgb(234, 91, 11)` → `#ea5b0b`. Browser geben Inline-Farben als `rgb(…)`
 * zurück; `<font color>` versteht nur Hex oder Farbnamen.
 */
export function cssColorToHex(value: string): string | null {
  const v = value.trim();
  if (!v) return null;

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(v);
  if (hex) {
    const h = hex[1]!;
    return h.length === 3
      ? `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase()
      : `#${h.toLowerCase()}`;
  }

  const rgb = /^rgba?\(\s*(\d+)\s*[, ]\s*(\d+)\s*[, ]\s*(\d+)/i.exec(v);
  if (rgb) {
    const toPart = (n: string) =>
      Math.min(255, Number(n)).toString(16).padStart(2, "0");
    return `#${toPart(rgb[1]!)}${toPart(rgb[2]!)}${toPart(rgb[3]!)}`;
  }

  // Farbnamen („red") reicht Outlook durch — alles andere (z. B. `color-mix`)
  // lieber auslassen als falsch übersetzen.
  return /^[a-z]+$/i.test(v) ? v.toLowerCase() : null;
}

/**
 * Der Wert von `color` aus einem `style`-Attribut — wie `el.style.color`.
 *
 * Die letzte gültige Angabe gewinnt, `!important` zählt mit, `background-color`
 * und Ähnliches sind nicht gemeint. Werte, mit denen `cssColorToHex` nichts
 * anfangen kann, fallen dort heraus.
 */
function styleColor(style: string | undefined): string {
  if (!style) return "";
  let farbe = "";
  let wichtig = false;
  for (const deklaration of style.split(";")) {
    const doppelpunkt = deklaration.indexOf(":");
    if (doppelpunkt < 0) continue;
    if (deklaration.slice(0, doppelpunkt).trim().toLowerCase() !== "color") continue;
    let wert = deklaration.slice(doppelpunkt + 1).trim();
    const istWichtig = /!\s*important$/i.test(wert);
    if (istWichtig) wert = wert.replace(/!\s*important$/i, "").trim();
    if (!wert || (wichtig && !istWichtig)) continue;
    farbe = wert;
    wichtig = istWichtig;
  }
  return farbe;
}

/** `textContent` ohne DOM: aller Text darunter, ohne Kommentare. */
function textInhalt(node: ChildNode): string {
  if (isText(node)) return node.data;
  if (node instanceof Element) return node.children.map(textInhalt).join("");
  return "";
}

/** Alle Elemente in Dokumentreihenfolge — wie `querySelectorAll("*")`. */
function alleElemente(nodes: ChildNode[], into: Element[] = []): Element[] {
  for (const n of nodes) {
    if (!isTag(n)) continue;
    into.push(n);
    alleElemente(n.children, into);
  }
  return into;
}

/**
 * Jede farbige Textstelle zusätzlich in `<font color>` fassen. Das CSS bleibt
 * stehen — im Browser gewinnt es, in Outlook das Attribut.
 */
export function toOutlookHtml(html: string): string {
  const doc = parseDocument(html);

  // Die Liste vorab einfrieren, wie zuvor `Array.from(querySelectorAll)`:
  // Die eingefügten `<font>` selbst werden nicht noch einmal besucht.
  for (const el of alleElemente(doc.children)) {
    if (!WRAPPABLE.has(el.name)) continue;

    const hex = cssColorToHex(styleColor(el.attribs["style"]));
    if (!hex) continue;

    // Abstandshalter (`&nbsp;`, farbige Balken) tragen keinen Text.
    const text = textInhalt(el);
    if (!text || text.trim() === "") continue;

    // Schon gesetzt — mehrfaches Aufbereiten darf nichts verschachteln.
    const only = el.children.length === 1 ? el.children[0] : null;
    if (only && isTag(only) && only.name === "font") continue;

    const kinder = el.children;
    const font = new Element("font", { color: hex }, kinder);
    for (const kind of kinder) kind.parent = font;
    // Geschwisterverweise innerhalb der Kinder bleiben gültig; nur der Rahmen
    // wechselt.
    font.parent = el;
    el.children = [font];
  }

  return render(doc, { encodeEntities: "utf8", emptyAttrs: true });
}
